/**
 * Chat Session Store — the durable, multi-conversation state behind NORA
 * (ChatPanel). Previously the panel held a single `messages` array in component
 * state, so a user was confined to one throwaway conversation that vanished on
 * reload. This store gives NORA a browser-like model:
 *
 *   • many conversations, each a `ChatSession`;
 *   • a working set of OPEN tabs (`openIds`) the user switches between;
 *   • the full HISTORY of every conversation — closing a tab keeps its session
 *     in history (revisitable), it does not destroy it; only an explicit delete
 *     removes a conversation.
 *
 * Everything survives a reload. Mirrors the notificationCenterStore pattern:
 * Zustand + `persist`, a hard cap so it can't grow unbounded, and return-stable
 * selectors so an unrelated write doesn't churn subscribers.
 *
 * Only the sessions + tab set + active id are persisted; the ephemeral streaming
 * state (in-flight tokens, tool status, errors) stays in the component because it
 * describes a request, not a conversation.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatLink } from '../components/shared/chatSse';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  links?: ChatLink[];
  followups?: string[];
}

export interface ChatSession {
  id: string;
  /** Human label for the tab/history row. Auto-derived from the first user
   *  message; falls back to DEFAULT_TITLE until the user has said something. */
  title: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

// ─── Config ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'stationExplorer_chatSessions';
/** Hard cap on retained sessions — the oldest inactive/closed ones are trimmed
 *  first (open tabs are always kept). */
const MAX_SESSIONS = 50;
export const DEFAULT_TITLE = 'New chat';
// A generous storage cap — NOT the display width. The tab/history label clips to
// its box with a CSS ellipsis, and the tab title scrolls the full stored text on
// hover, so baking a truncating "…" into the stored title would make the marquee
// reveal a dead ellipsis instead of the real words. Keep enough to be meaningful
// on hover without retaining an unbounded string.
const TITLE_MAX_LEN = 120;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Reasonably-unique id without a hard dependency on crypto.randomUUID (absent in
 *  some jsdom setups). Time + entropy is plenty for a client-side session key. */
function makeId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** A tab/history label from the first user turn: collapsed whitespace, capped to
 *  a storage limit (NOT visually truncated — the UI clips with a CSS ellipsis and
 *  scrolls the full text on hover, so no "…" is baked in). Returns null if there's
 *  no user text to derive from yet. */
function deriveTitle(messages: ChatMessage[]): string | null {
  const firstUser = messages.find((m) => m.role === 'user' && m.content.trim());
  if (!firstUser) return null;
  const clean = firstUser.content.trim().replace(/\s+/g, ' ');
  return clean.length > TITLE_MAX_LEN ? clean.slice(0, TITLE_MAX_LEN).trimEnd() : clean;
}

function makeSession(at: number): ChatSession {
  return { id: makeId(), title: DEFAULT_TITLE, messages: [], createdAt: at, updatedAt: at };
}

/** Enforce MAX_SESSIONS, keeping every open tab plus the most-recently-active of
 *  the rest. Returns the possibly-trimmed session list. */
function trim(sessions: ChatSession[], openIds: string[]): ChatSession[] {
  if (sessions.length <= MAX_SESSIONS) return sessions;
  const open = new Set(openIds);
  const pinned = sessions.filter((s) => open.has(s.id));
  const rest = sessions
    .filter((s) => !open.has(s.id))
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, Math.max(0, MAX_SESSIONS - pinned.length));
  const keep = new Set([...pinned, ...rest].map((s) => s.id));
  return sessions.filter((s) => keep.has(s.id));
}

// ─── Store ───────────────────────────────────────────────────────────────────

interface ChatSessionState {
  sessions: ChatSession[];
  /** Ids of the sessions surfaced as tabs, in tab (left-to-right) order. */
  openIds: string[];
  activeId: string | null;
}

interface ChatSessionActions {
  /** Ensure there's an active, OPEN session (create/adopt one if needed). Called
   *  when the panel opens so it always has somewhere to write. Returns the id. */
  ensureActive: () => string;
  /** Create a fresh empty session, open it as a tab, and make it active. If the
   *  active tab is already an untouched blank, reuse it instead of piling up
   *  empty "New chat" tabs. Returns the active id. */
  newSession: () => string;
  /** Re-open a session from history as a tab and activate it. */
  openSession: (id: string) => void;
  /** Switch the active tab (must reference an existing session; opens it if it
   *  wasn't already a tab). */
  setActive: (id: string) => void;
  /** Close a tab WITHOUT deleting its conversation — it stays in history. If it
   *  was active, fall back to the nearest remaining open tab (or ensure one). */
  closeTab: (id: string) => void;
  /** Permanently delete a conversation (from tabs AND history). */
  deleteSession: (id: string) => void;
  renameSession: (id: string, title: string) => void;
  /** Functional update of one session's messages (used while streaming). Bumps
   *  updatedAt and back-fills the title from the first user turn. No-op if the
   *  session id is unknown (e.g. deleted mid-stream). */
  setMessages: (id: string, updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  /** Wipe every conversation and start one fresh open session. */
  clearAll: () => void;
}

type ChatSessionStore = ChatSessionState & ChatSessionActions;

/** Pick the id to fall back to after the active tab goes away: the last-opened
 *  remaining tab, else null. */
function fallbackActive(openIds: string[]): string | null {
  return openIds.length > 0 ? openIds[openIds.length - 1] : null;
}

export const useChatSessionStore = create<ChatSessionStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      openIds: [],
      activeId: null,

      ensureActive: () => {
        const { sessions, openIds, activeId } = get();
        if (activeId && openIds.includes(activeId) && sessions.some((s) => s.id === activeId)) {
          return activeId;
        }
        // An open tab exists but nothing valid is active → adopt the last one.
        const validOpen = openIds.filter((id) => sessions.some((s) => s.id === id));
        if (validOpen.length > 0) {
          const next = validOpen[validOpen.length - 1];
          set({ openIds: validOpen, activeId: next });
          return next;
        }
        // Nothing open: adopt the most-recent existing session, else create one.
        if (sessions.length > 0) {
          const latest = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
          set({ openIds: [latest.id], activeId: latest.id });
          return latest.id;
        }
        const fresh = makeSession(Date.now());
        set({ sessions: [fresh], openIds: [fresh.id], activeId: fresh.id });
        return fresh.id;
      },

      newSession: () => {
        const { sessions, openIds, activeId } = get();
        // Don't pile up empty blanks: if the active tab is untouched, reuse it.
        const active = sessions.find((s) => s.id === activeId);
        if (active && active.messages.length === 0 && openIds.includes(active.id)) {
          return active.id;
        }
        const fresh = makeSession(Date.now());
        set({
          sessions: trim([...sessions, fresh], [...openIds, fresh.id]),
          openIds: [...openIds, fresh.id],
          activeId: fresh.id,
        });
        return fresh.id;
      },

      openSession: (id) =>
        set((state) => {
          if (!state.sessions.some((s) => s.id === id)) return {};
          return {
            openIds: state.openIds.includes(id) ? state.openIds : [...state.openIds, id],
            activeId: id,
          };
        }),

      setActive: (id) =>
        set((state) => {
          if (!state.sessions.some((s) => s.id === id)) return {};
          return {
            openIds: state.openIds.includes(id) ? state.openIds : [...state.openIds, id],
            activeId: id,
          };
        }),

      closeTab: (id) =>
        set((state) => {
          const openIds = state.openIds.filter((x) => x !== id);
          if (state.activeId !== id) return { openIds };
          const next = fallbackActive(openIds);
          if (next) return { openIds, activeId: next };
          // Closed the last tab — reopen the most-recent conversation so the panel
          // never lands on a blank void. Closing never deletes, so `sessions` is
          // intact and the just-closed conversation is still a candidate.
          if (state.sessions.length > 0) {
            const latest = [...state.sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
            return { openIds: [latest.id], activeId: latest.id };
          }
          const fresh = makeSession(Date.now());
          return { sessions: [fresh], openIds: [fresh.id], activeId: fresh.id };
        }),

      deleteSession: (id) =>
        set((state) => {
          const sessions = state.sessions.filter((s) => s.id !== id);
          const openIds = state.openIds.filter((x) => x !== id);
          if (state.activeId !== id) return { sessions, openIds };
          const next = fallbackActive(openIds);
          if (next) return { sessions, openIds, activeId: next };
          if (sessions.length > 0) {
            const latest = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
            return { sessions, openIds: [latest.id], activeId: latest.id };
          }
          const fresh = makeSession(Date.now());
          return { sessions: [fresh], openIds: [fresh.id], activeId: fresh.id };
        }),

      renameSession: (id, title) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, title: title.trim() || DEFAULT_TITLE } : s,
          ),
        })),

      setMessages: (id, updater) =>
        set((state) => {
          if (!state.sessions.some((s) => s.id === id)) return {};
          return {
            sessions: state.sessions.map((s) => {
              if (s.id !== id) return s;
              const messages = updater(s.messages);
              const title = s.title === DEFAULT_TITLE ? deriveTitle(messages) ?? s.title : s.title;
              return { ...s, messages, title, updatedAt: Date.now() };
            }),
          };
        }),

      clearAll: () => {
        const fresh = makeSession(Date.now());
        set({ sessions: [fresh], openIds: [fresh.id], activeId: fresh.id });
      },
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      // Persist the conversations + tab set + active id; actions are re-created.
      partialize: (state) => ({
        sessions: state.sessions,
        openIds: state.openIds,
        activeId: state.activeId,
      }),
      // Validate rehydrated state before merging. Zustand's default merge is a
      // shallow spread, so a same-version but MALFORMED persisted value (e.g.
      // sessions === null from a divergent build, an extension, or tampering)
      // would overwrite our `sessions: []` with a non-array and crash the first
      // render of the app-wide ChatPanel (selectors/`.map`/`.find` on non-array).
      // Coerce to well-formed shapes so a corrupt key degrades gracefully —
      // ensureActive() then recreates a session — instead of bricking the app.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ChatSessionState>;
        const sessions = Array.isArray(p.sessions)
          ? p.sessions.filter(
              (s): s is ChatSession =>
                !!s &&
                typeof (s as ChatSession).id === 'string' &&
                Array.isArray((s as ChatSession).messages),
            )
          : [];
        const ids = new Set(sessions.map((s) => s.id));
        const openIds = Array.isArray(p.openIds) ? p.openIds.filter((id) => ids.has(id)) : [];
        const activeId = typeof p.activeId === 'string' && ids.has(p.activeId) ? p.activeId : null;
        return { ...current, sessions, openIds, activeId };
      },
    },
  ),
);

// ─── Selectors ───────────────────────────────────────────────────────────────
// Return-stable selectors: Zustand v5 (useSyncExternalStore) re-renders whenever
// a selector returns a new reference. Derived arrays are memoised on input
// identity so an unrelated write doesn't churn subscribers.

/** The active session object (stable ref until that session changes). */
export const selectActiveSession = (state: ChatSessionState): ChatSession | undefined =>
  state.sessions.find((s) => s.id === state.activeId);

let _tabsSessions: ChatSession[] = [];
let _tabsOpenIds: string[] = [];
let _tabs: ChatSession[] = [];
/** The open tabs, in tab order (openIds order). Stable ref until the tab set or
 *  its sessions change. */
export const selectOpenTabs = (state: ChatSessionState): ChatSession[] => {
  if (state.sessions === _tabsSessions && state.openIds === _tabsOpenIds) return _tabs;
  _tabsSessions = state.sessions;
  _tabsOpenIds = state.openIds;
  const byId = new Map(state.sessions.map((s) => [s.id, s]));
  _tabs = state.openIds.map((id) => byId.get(id)).filter((s): s is ChatSession => Boolean(s));
  return _tabs;
};

let _historyInput: ChatSession[] = [];
let _history: ChatSession[] = [];
/** All conversations for the history panel, most-recently-active first. */
export const selectHistory = (state: ChatSessionState): ChatSession[] => {
  if (state.sessions === _historyInput) return _history;
  _historyInput = state.sessions;
  _history = [...state.sessions].sort((a, b) => b.updatedAt - a.updatedAt);
  return _history;
};
